// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import styles from './UserSecurity.module.scss';
import { useTranslation } from 'react-i18next';
import { IconEditIC } from 'components/atoms/icons/IconEditIC';
import { useEffect, useMemo, useState } from 'react';
import EditUserModal, {
  renderMFATooltip,
  UserInput,
} from 'components/organisms/EditUserModal';
import Toggle from 'components/atoms/Toggle';
import CustomPagination from 'components/atoms/CustomPagination';
import { Account, ResponseAccount } from 'util/accountManagementTypes';
import { createAxios } from 'ts/createAxios';
import { handleError } from 'util/errorHandler';
import { toast } from 'react-toastify';
import Popover from 'components/atoms/Popover';
import { IconInfo } from 'components/atoms/icons/IconInfo';
import { OperatorType, RoleType, ScaleType } from 'util/Enums';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';

const RECORDS_OF_PAGE = 20;

const UserSecurityList = () => {
  const { t } = useTranslation();
  const [editData, setEditData] = useState<Account>();
  const [data, setData] = useState<ResponseAccount>({
    page: 1,
    total: 0,
    users: [],
  });
  const pageCount = Math.ceil(data.total / RECORDS_OF_PAGE);
  const user = useSelector(selectUser);

  const fetchUsers = async (page?: number) => {
    const api = createAxios();

    try {
      const res = await api.get<ResponseAccount>('/users', {
        params: {
          page,
          take: RECORDS_OF_PAGE,
        },
      });

      if (res) {
        setData(res.data);
      }
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const canViewScaleSettings = useMemo(() => {
    if (user.operatorType === OperatorType.Compressions) {
      return user.role === RoleType.AdminOperator;
    }

    if (user.operatorType === OperatorType.Emissions) {
      return (
        user.role &&
        [
          RoleType.AdminOperator,
          RoleType.AdminSite,
          RoleType.UserNormal,
        ].includes(user.role)
      );
    }

    return false;
  }, [user]);

  const handlePageClick = (selectedItem: { selected: number }) => {
    fetchUsers(selectedItem.selected + 1);
  };

  const handleEdit = (form: UserInput, id: number | string) => {
    const api = createAxios();

    api
      .put(`/users/${id}`, {
        ...form,
        email: form.email || '',
      })
      .then(() => {
        fetchUsers(data.page);
        setEditData(undefined);
        toast.success(t('messages.M_011'));
      })
      .catch((error) => {
        handleError(error);
      });
  };

  return (
    <div className={styles.userSecurity}>
      <div className={styles.userSecurityContent}>
        <div className={styles.title}>
          <div
            className={styles.countItem}
            dangerouslySetInnerHTML={{
              __html:
                t('common.page', {
                  page: data?.total || 0,
                }) ?? '',
            }}
          />
        </div>
        <div className={styles.table}>
          <table id={styles.userSecurityTable}>
            <thead>
              <tr>
                <th className={styles.userId}>{t('user_security.label_id')}</th>
                {canViewScaleSettings && (
                  <>
                    <th className={styles.scaleType}>
                      {t('user_security.label_scale_type')}
                    </th>
                    <th className={styles.scaleModel}>
                      {t('user_security.label_scale_model')}
                    </th>
                  </>
                )}
                <th className={styles.email}>
                  {t('user_security.label_email')}
                </th>
                <th className={styles.mfa}>
                  <div className={styles.mfaLabel}>
                    {t('user_security.label_mfa')}

                    <Popover
                      content={renderMFATooltip(t)}
                      arrowContainerConfigs={{
                        arrowColor: '#4d4d4d',
                      }}
                      popUpStyle={{
                        background: '#4d4d4d',
                        maxWidth: '500px',
                        whiteSpace: 'pre-line',
                      }}
                      containerStyle={{
                        zIndex: '100000',
                      }}
                    >
                      <span>
                        <IconInfo />
                      </span>
                    </Popover>
                  </div>
                </th>
                <th className={styles.actionField}>
                  {t('user_security.label_action')}
                </th>
              </tr>
            </thead>
            <tbody>
              {data.users && data.users.length > 0 ? (
                data.users.map((item, i) => {
                  return (
                    <tr key={i}>
                      <td className={styles.userId}>
                        <p>{item.id}</p>
                      </td>
                      {canViewScaleSettings && (
                        <>
                          <td className={styles.scaleType}>
                            <p>
                              {item.scalesType
                                ? t(
                                    `user_security.scale_model.${item.scalesType.toLowerCase()}`
                                  )
                                : ''}
                            </p>
                          </td>
                          <td className={styles.scaleModel}>
                            <p>
                              {item.scalesType &&
                              item.scalesType !== ScaleType.NONE &&
                              item.scalesModel == null
                                ? t('user_security.unidentified_scale_model')
                                : item.scalesModel?.name}
                            </p>
                          </td>
                        </>
                      )}
                      <td className={styles.email}>
                        <p>{item.email}</p>
                      </td>
                      <td className={styles.mfa}>
                        <Toggle value={item.mfaEnable} disabled isDarkMode />
                      </td>
                      <td className={styles.actionField1}>
                        <div className={styles.contentAction}>
                          <div
                            className={styles.actionEdit}
                            onClick={() => {
                              setEditData(item);
                            }}
                          >
                            <IconEditIC />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={canViewScaleSettings ? 6 : 4}
                    className={styles.msgError}
                  >
                    {t('messages.M_008')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className={styles.Pagination}>
          {pageCount > 1 && (
            <CustomPagination
              pageCount={pageCount}
              onPageChange={handlePageClick}
              forcePage={Math.max(data.page - 1, 0)}
            />
          )}
        </div>
      </div>

      {editData && (
        <EditUserModal
          onClose={() => {
            setEditData(undefined);
          }}
          userData={editData}
          onSubmit={(data, id) => handleEdit(data, id)}
        />
      )}
    </div>
  );
};

export default UserSecurityList;
